/**
 * Unified History
 * ===============
 * Merges the two separate localStorage stores — `chat-history.ts` (full
 * conversational transcripts) and `search-history.ts` (completed one-shot
 * searches) — into a single chronological feed for the history panel.
 * Read-only projection: writes still go through each store's own helpers.
 */

import {
  ChatTranscript,
  deleteChatTranscript,
  deleteChatTranscripts,
  getChatTranscripts,
} from './chat-history';
import {
  SearchHistoryItem,
  getSearchHistory,
  removeFromSearchHistory,
  removeManyFromSearchHistory,
} from './search-history';

export type UnifiedHistoryEntry =
  | {
      type: 'chat';
      /** ChatTranscript.id — needed to resume or delete. */
      sourceId: string;
      label: string;
      timestamp: number;
      completed: boolean;
      lastTurnIndex: number;
    }
  | {
      type: 'search';
      /** SearchHistoryItem.id — needed to delete. */
      sourceId: string;
      label: string;
      timestamp: number;
    };

function labelForTranscript(transcript: ChatTranscript): string {
  const lastTurn = transcript.turns[transcript.turns.length - 1];
  return (transcript.finalSearchQuery || lastTurn?.parsed.searchQuery || transcript.initialUserInput || '').trim();
}

/**
 * History is scoped by COUNTRY, not by language: `en-CA` and `fr-CA` share one
 * history, as do `en-UK` / `fr-UK` / `cy-UK` / `ga-UK` / `gd-UK`. A bilingual
 * shopper switching languages mid-session keeps everything they just did.
 *
 * Stored records carry the full locale (`<lang>-<REGION>`, plus the `pt-BR` /
 * `pt-PT` special cases), so everything after the first dash is the region
 * code — the same value `LocaleDetails.regionCode` holds for the live locale.
 */
function localeCountry(locale: string): string {
  const dash = locale.indexOf('-');
  return dash === -1 ? locale : locale.slice(dash + 1);
}

/** Every persisted chat for this country, newest first, both languages. */
export function getChatTranscriptsForCountry(regionCode: string): ChatTranscript[] {
  return getChatTranscripts().filter((t) => localeCountry(t.locale) === regionCode);
}

/** Every completed one-shot search for this country, both languages. */
export function getSearchHistoryForCountry(regionCode: string): SearchHistoryItem[] {
  return getSearchHistory().filter((s) => localeCountry(s.locale) === regionCode);
}

export function clearChatHistoryForCountry(regionCode: string): void {
  deleteChatTranscripts(getChatTranscriptsForCountry(regionCode).map((t) => t.id));
}

export function clearSearchHistoryForCountry(regionCode: string): void {
  removeManyFromSearchHistory(getSearchHistoryForCountry(regionCode).map((s) => s.id));
}

export function getUnifiedHistory(regionCode: string): UnifiedHistoryEntry[] {
  const chats: UnifiedHistoryEntry[] = getChatTranscriptsForCountry(regionCode)
    .filter((t) => t.turns.length > 0)
    .map((t) => ({
      type: 'chat' as const,
      sourceId: t.id,
      label: labelForTranscript(t),
      timestamp: t.updatedAt,
      completed: !!t.finalHref,
      lastTurnIndex: t.turns[t.turns.length - 1].index,
    }))
    .filter((entry) => entry.label);

  const searches: UnifiedHistoryEntry[] = getSearchHistoryForCountry(regionCode).map((s) => ({
    type: 'search' as const,
    sourceId: s.id,
    label: s.searchQuery,
    timestamp: s.timestamp,
  }));

  return [...chats, ...searches].sort((a, b) => b.timestamp - a.timestamp);
}

export function deleteUnifiedHistoryEntry(entry: UnifiedHistoryEntry): void {
  if (entry.type === 'chat') {
    deleteChatTranscript(entry.sourceId);
  } else {
    removeFromSearchHistory(entry.sourceId);
  }
}

/**
 * Clears every entry for this country — both languages of `*-CA`, etc. Routed
 * through the bulk deletes so the whole wipe costs one storage round-trip per
 * store rather than one per entry.
 */
export function clearUnifiedHistory(regionCode: string): void {
  clearChatHistoryForCountry(regionCode);
  clearSearchHistoryForCountry(regionCode);
}
